/* ═══════════════════════════════════════════════════════════════
   Forma Studio — the forma panel bodies (Levels, Layout Options, Tokens)
   and the shared literal-text dialog.

   Mounted by EVERY forma editor through useFormaEditorPanels — Forma
   Studio and the project editor (Designer) show these exact bodies. Each
   is the shared kit component (LevelProps, DocSettings, TokenRail,
   ProjectTokenChips + FormaPlaceholderBindings) writing through the host's
   forma draft. There is no Header & Footer panel: zones are worked on the
   paper (paper/ZoneBandEdit.tsx).
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/react';
import type { StandardConfig } from '../../../shared/types';
import { caretLevelOf } from '../../editor-chrome/ToolbarControls';
import { DocSettings } from '../../components/EditorComponents';
import { LevelProps } from '../../components/LevelProps';
import { ProjectTokenChips, TokenRail } from '../TokenComponents';
import { FormaPlaceholderBindings } from '../../components/FormaPlaceholderBindings';
import {
  nextPlaceholderToken,
  usedPlaceholderIds,
  ISSUANCE_DATE_ID,
  type PlaceholderContext,
} from '../../../shared/forma-placeholders';
import { defaultNumberFormats } from '../../utils';
import { isCsiDefault, resetLevelToCsi } from '../../../shared/csi-defaults';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Hint } from '../../components/ui/tooltip';
import { Info } from 'lucide-react';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';
import type { PageVariant } from '../formaZonesModel';
import { appLayerProps } from '../../components/ui/app-layer';

export type FormaEdit = (fn: (f: StandardConfig) => StandardConfig) => void;

const PALETTE_HINT: React.CSSProperties = {
  padding: 'var(--space-4)',
  fontSize: 'var(--font-size-sm)',
  lineHeight: 1.55,
  color: 'var(--color-text-muted)',
};

/** One-line version of PALETTE_HINT: the empty-state nudge that stays in the
 *  body, with the full explanation on the section's ⓘ tooltip. */
const PALETTE_HINT_LINE: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3) var(--space-4)',
  fontSize: 'var(--font-size-sm)',
  lineHeight: 1.45,
  color: 'var(--color-text-muted)',
};

const PALETTE_HOST: React.CSSProperties = {
  width: '100%',
  height: '100%',
  overflow: 'auto',
  background: 'var(--sf)',
};

/* ─── Levels — a sub-tool, not a mode ───
   By the time a spec is in a project its levels are already defined, so this
   palette is something you reach for: it follows the caret's level by default
   and lets you point it anywhere with the 1–9 picker. Nothing about clicking
   body text changes intent — that always just places the caret. */

export function LevelPalette({
  forma,
  editor,
  levelFocus,
  onLevel,
  onEdit,
  disabled,
  formatBar,
}: {
  forma: StandardConfig;
  /** The live document — the palette follows the caret's level by default. */
  editor: Editor | null;
  /** Non-null when the user pointed the palette at a level deliberately. */
  levelFocus: number | null;
  onLevel: (level: number) => void;
  onEdit: FormaEdit;
  disabled?: boolean;
  /** Forma Studio only: the Format Bar owns character/font/alignment/spacing. */
  formatBar?: boolean;
}) {
  // Follow the caret without making the whole workspace re-render: only this
  // palette subscribes to the editor.
  const [, tick] = React.useReducer((n: number) => n + 1, 0);
  React.useEffect(() => {
    if (!editor) return;
    editor.on('selectionUpdate', tick);
    return () => {
      editor.off('selectionUpdate', tick);
    };
  }, [editor]);

  const level = levelFocus ?? caretLevelOf(editor) ?? 1;
  const idx = Math.max(0, Math.min(8, level - 1));
  const target = forma.levels?.[idx];
  return (
    <div style={PALETTE_HOST}>
      <div className="dsg-levelpicker" role="group" aria-label="Level to style">
        <span className="dsg-levelpicker-label">Level</span>
        {Array.from({ length: 9 }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            className={`dsg-levelpicker-btn${n === level ? ' is-active' : ''}`}
            aria-pressed={n === level}
            onClick={() => onLevel(n)}
            title={`Style every level ${n} paragraph`}
          >
            {n}
          </button>
        ))}
      </div>
      {target ? (
        <>
          {/* CSI SectionFormat defaults (contract §3 R6, item 1) — visibly
             labeled as defaults, never silent: a lay person needs to know
             whether a spacing/indent value is the published standard or a
             firm decision before they touch it. */}
          <div className={`dsg-csi-banner${isCsiDefault(target) ? ' is-default' : ' is-custom'}`}>
            <span className="dsg-csi-banner-label">
              {isCsiDefault(target) ? 'CSI SectionFormat default' : 'Custom — differs from CSI'}
            </span>
            {!isCsiDefault(target) && (
              <button
                type="button"
                className="dsg-csi-reset-btn"
                disabled={disabled}
                onClick={() =>
                  onEdit((f) => {
                    const levels = [...f.levels];
                    levels[idx] = resetLevelToCsi(levels[idx]);
                    return { ...f, levels };
                  })
                }
                title="Put this level's spacing and indents back to the published CSI SectionFormat convention. Bold, caps, colour and justification are untouched — those are the firm's, not the standard's."
              >
                Reset to CSI default
              </button>
            )}
          </div>
          <fieldset className="dsg-palette-fieldset" disabled={disabled}>
            <LevelProps
              level={target}
              levelIdx={idx}
              onUpdate={(u: unknown) =>
                onEdit((f) => {
                  const levels = [...f.levels];
                  levels[idx] = u as (typeof levels)[number];
                  return { ...f, levels };
                })
              }
              formatBar={formatBar}
              bodyFont={forma.docSettings?.bodyFont}
              bodyFontSize={forma.docSettings?.bodyFontSize}
              numFormats={forma.special?.numberFormats || defaultNumberFormats()}
              onNumFmtChange={(i: number, fmt: string) =>
                onEdit((f) => {
                  const nf = [...(f.special?.numberFormats || defaultNumberFormats())];
                  nf[i] = fmt;
                  return { ...f, special: { ...f.special, numberFormats: nf } };
                })
              }
            />
          </fieldset>
        </>
      ) : (
        <div style={PALETTE_HINT}>
          This forma does not define level {level} yet. Pick another level, or add it in the master
          forma.
        </div>
      )}
    </div>
  );
}

/* ─── Layout Options — the same DocSettings section the classic view docks ─── */

export function LayoutPalette({
  forma,
  onEdit,
  pageVariant,
  onPageVariant,
}: {
  forma: StandardConfig;
  onEdit: FormaEdit;
  pageVariant: PageVariant;
  onPageVariant: (v: PageVariant) => void;
}) {
  return (
    <div style={PALETTE_HOST}>
      <DocSettings
        doc={forma.docSettings}
        onUpdate={(d: StandardConfig['docSettings']) => onEdit((f) => ({ ...f, docSettings: d }))}
        sections={['Layout Options']}
        pageVariant={pageVariant}
        onPageVariant={(v: string) => onPageVariant(v as PageVariant)}
      />
    </div>
  );
}

/* ─── Tokens — ONE palette, two labeled sections (Josh, 2026-08-26 — merges
       the separate "Project Tokens" palette shipped v2.14.47 into this one;
       same chips, same drag/click behaviour, one home):
         • Field Tokens   — the shared firm token rail (fields, elements,
                             custom tokens).
         • Project Tokens — the numbered {P#} placeholder chips and their
                             binding controls, when a forma is open.

   LAYOUT LAW (Josh, 2026-08-26 — the occlusion bug): the body is ONE
   normally-flowing column inside ONE scroll container. No section carries a
   percentage height, a flex ratio, or its own scroller — the first merge
   capped Field Tokens at 42% and gave both halves competing flex/min-height
   rules, so squeezing the panel short made the sections fight over the same
   pixels and Project Tokens drew on top of the rail above it. Everything
   here is natural height; when the panel runs short the whole column simply
   scrolls. Keep it that way. ─── */

/** Sub-header of a Tokens section. `info` adds the one explainer affordance:
 *  a Hint (kit tooltip, ui/tooltip.tsx) on an icon button — long-form copy
 *  belongs in a tooltip, not in the panel body (Josh, 2026-08-26). */
function TokensSectionLabel({
  children,
  info,
  divider,
}: {
  children: React.ReactNode;
  info?: string;
  divider?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-1)',
        padding: `${rem(10)} var(--space-3) ${rem(2)}`,
        marginTop: divider ? 'var(--space-2)' : undefined,
        borderTop: divider ? '1px solid var(--bd)' : undefined,
        fontSize: 'var(--font-size-sm)',
        fontWeight: 700,
        letterSpacing: '.07em',
        textTransform: 'uppercase',
        color: 'var(--tm)',
      }}
    >
      {children}
      {info && (
        <Hint text={info} side="bottom">
          <button
            type="button"
            aria-label={`About ${typeof children === 'string' ? children : 'this section'}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              border: 'none',
              background: 'transparent',
              color: 'var(--tm)',
              cursor: 'help',
              lineHeight: 0,
            }}
          >
            <Icon icon={Info} em={1.1818} aria-hidden />
          </button>
        </Hint>
      )}
    </div>
  );
}

/* The long-form explainers that used to eat the bottom half of the panel.
   Same words, now tooltip copy; the body keeps one short line. */
const MASTER_TOKENS_TIP =
  'A master defines the blanks. Drag the Placeholder chip into a header or footer wherever this client’s templates carry project-specific text — each {P#} remembers what was there. Every project that uses this master fills its blanks with that project’s own values.';
const PROJECT_TOKENS_TIP =
  'This spec still prints with a master forma. The first formatting change you make forks this project’s own copy — then each placeholder gets its value here.';

export function TokensPalette({
  savedTokens,
  recentTokens,
  onSaveToken,
  onRemoveSavedToken,
  onDragState,
  onInsert,
  forma,
  ctx,
  projectTokens,
  issuanceNames,
  onEdit,
  masterAuthoring,
}: {
  savedTokens: string[];
  recentTokens: string[];
  onSaveToken: (t: string) => void;
  onRemoveSavedToken: (t: string) => void;
  onDragState: (on: boolean) => void;
  onInsert?: (value: string) => void;
  /** The Project Tokens section renders only once a forma is open — absent
   *  (e.g. during load) simply omits that section, same as before the merge
   *  when its palette rendered nothing. */
  forma?: StandardConfig;
  ctx?: PlaceholderContext;
  projectTokens?: Array<{ id: string; name: string; content: string }>;
  issuanceNames?: string[];
  onEdit?: FormaEdit;
  /** True in the Master Forma Studio: the user is AUTHORING the master, so
   *  the hint explains defining {P#} blanks — not the Designer's "still
   *  prints with a master" copy, which describes a PROJECT spec that hasn't
   *  forked its own forma yet and is nonsense in the Studio (Josh,
   *  2026-08-13). */
  masterAuthoring?: boolean;
}) {
  /* The explainer only ever applied to a forma that has no project of its own
     (a master being authored, or a project spec still printing with one) —
     unchanged from before; it just lives in the tooltip now. */
  const explainerTip =
    forma && !forma.projectId
      ? masterAuthoring
        ? MASTER_TOKENS_TIP
        : PROJECT_TOKENS_TIP
      : undefined;

  // Each section is ONE natural-height group (LAYOUT LAW above still holds —
  // nothing here carries a percentage height or a competing flex ratio); the
  // outer sf-palette-flow just lets the groups tile side-by-side instead of
  // stacking when the palette is docked top/bottom (DESIGN.md §3.5).
  return (
    <div
      style={{ ...PALETTE_HOST, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      className="sf-palette-flow"
    >
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <TokensSectionLabel>Field Tokens</TokensSectionLabel>
        <TokenRail
          savedTokens={savedTokens}
          recentTokens={recentTokens}
          onSave={onSaveToken}
          onRemoveSaved={onRemoveSavedToken}
          projectTokens={projectTokens}
          onStart={() => onDragState(true)}
          onEnd={() => onDragState(false)}
          onInsert={onInsert}
        />
      </div>

      {forma && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <TokensSectionLabel info={explainerTip} divider>
            Project Tokens
          </TokensSectionLabel>
          <ProjectTokenChips
            nextPlaceholder={nextPlaceholderToken(forma)}
            usedPlaceholders={usedPlaceholderIds(forma).filter((id) => id !== ISSUANCE_DATE_ID)}
            onStart={() => onDragState(true)}
            onEnd={() => onDragState(false)}
            onInsert={onInsert}
          />
          {forma.projectId ? (
            <FormaPlaceholderBindings
              config={forma}
              ctx={ctx || {}}
              issuanceNames={issuanceNames || []}
              onChange={(next) => onEdit?.((f) => ({ ...f, bindings: next }))}
            />
          ) : (
            /* One line, not a paragraph — the rest is on the ⓘ above. */
            <div style={PALETTE_HINT_LINE}>
              {masterAuthoring
                ? 'Drag Placeholder into a header or footer to define a blank.'
                : 'Prints with a master forma until your first formatting change.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── The literal-text request (paper drops, Edit text, quick-add) ─── */

export interface TextDialogRequest {
  title: string;
  initial: string;
  onCommit: (value: string) => void;
  /** Show a date picker that writes the chosen date into the text, formatted
   *  long-form — the classic canvas's "Use Date" modal, which is how a FIXED
   *  (frozen) date gets stamped into a zone. Distinct from the live `{Date}`
   *  token, which re-resolves every export. */
  datePicker?: boolean;
}

/* ─── Shared literal-text dialog (portalled, Esc, focus on open) ─── */

export function TextTokenDialog({
  request,
  onClose,
}: {
  request: TextDialogRequest;
  onClose: () => void;
}) {
  const [value, setValue] = React.useState(request.initial);
  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const commit = () => {
    // Free text: keep interior newlines, drop only surrounding blank space.
    const v = value.replace(/^\s+|\s+$/g, '');
    onClose();
    if (v) request.onCommit(v);
  };

  return createPortal(
    <div
      {...appLayerProps}
      role="dialog"
      aria-modal="true"
      aria-label={request.title}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'color-mix(in srgb, var(--color-bg) 55%, transparent)',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: rem(340),
          maxWidth: '90vw',
          background: 'var(--sf)',
          border: '1px solid var(--bd)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-xl)',
          padding: 'var(--space-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            letterSpacing: '.05em',
            textTransform: 'uppercase',
            color: 'var(--color-text-muted)',
          }}
        >
          {request.title}
        </span>
        {request.datePicker && (
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-1)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
            }}
          >
            Pick a date
            <Input
              type="date"
              aria-label="Pick a date to print"
              onChange={(e) => {
                if (!e.target.value) return;
                const d = new Date(e.target.value + 'T12:00:00');
                setValue(
                  d.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                );
              }}
            />
          </label>
        )}
        {/* FREE TEXT, not a one-line string (Josh, 2026-08-13). A header line
            is routinely multi-line — "Client Name / City, State" — and the
            zone model has always stored newlines. Enter makes a new line;
            Ctrl+Enter saves. */}
        <textarea
          ref={inputRef as unknown as React.RefObject<HTMLTextAreaElement>}
          className="sf-input dsg-freetext"
          value={value}
          rows={3}
          placeholder="Text to print — Enter for a new line"
          aria-label={request.datePicker ? 'Date as it will print' : 'Text to print'}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              commit();
            }
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={commit} disabled={!value.trim()} title="Ctrl+Enter">
            Save
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
